import type { AuthTokenPayload, AuthResponse } from "@mot-cards/types";
import { parseApiError } from "./api-error";
import { apiFetchPath } from "./api-fetch-path";
import { fetchApi } from "./api-fetch";
import { expiredSessionLoginPath } from "./auth-session-expired";

const USER_KEY = "mot_cards_user";

/** Notifies persistent client chrome that the authenticated identity changed. */
export const AUTH_SESSION_CHANGED_EVENT = "mot:auth-session-changed";

function dispatchAuthSessionChanged(): void {
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT));
  }
}

/* ------------------------------------------------------------------ */
/*  Token storage                                                     */
/* ------------------------------------------------------------------ */
/** Deprecated compatibility export. Browser code must not read auth credentials. */
export function getToken(): string | null {
  return null;
}

function setCurrentUser(user: AuthResponse["user"]): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(USER_KEY, JSON.stringify({ userId: user.id, email: user.email, role: user.role }));
  dispatchAuthSessionChanged();
}

/**
 * Reconcile the cached identity with the server session. The cookie session
 * can change without a web signin (another account, admin console), leaving
 * this cache pointing at the wrong user -- the collection page then fetches
 * a different account's (empty) collection. Callers that already hit
 * /api/auth/me pass its user here so the cache follows the session.
 */
export function syncCurrentUser(user: { id?: unknown; email?: unknown; role?: unknown }): boolean {
  if (typeof window === "undefined") return false;
  if (typeof user.id !== "string" || typeof user.email !== "string" || typeof user.role !== "string") return false;
  const current = getCurrentUser();
  if (current && current.userId === user.id && current.email === user.email && current.role === user.role) return false;
  setCurrentUser({ id: user.id, email: user.email, role: user.role } as AuthResponse["user"]);
  return true;
}

export function removeCurrentUser(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem(USER_KEY);
    dispatchAuthSessionChanged();
  }
}

/* ------------------------------------------------------------------ */
/*  User helpers                                                      */
/* ------------------------------------------------------------------ */
export function getCurrentUser(): AuthTokenPayload | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as AuthTokenPayload;
    return typeof value.userId === "string" && typeof value.email === "string" && typeof value.role === "string" ? value : null;
  } catch { return null; }
}

export function isAuthenticated(): boolean {
  return getCurrentUser() !== null;
}

/** Clear stale client identity and require a fresh secure server session. */
export function handleExpiredSession(): void {
  if (typeof window === "undefined") return;
  removeCurrentUser();
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.assign(expiredSessionLoginPath(currentPath));
}

/* ------------------------------------------------------------------ */
/*  API helpers                                                       */
/* ------------------------------------------------------------------ */
/**
 * A 401 surfaced to the caller instead of being turned into a navigation.
 *
 * Typed so a caller can tell "the session is gone" apart from a network
 * failure without sniffing messages — the wallet provider treats the two
 * differently (stale session vs. retry later).
 */
export class UnauthorizedError extends Error {
  readonly status = 401;
  constructor(path: string) {
    super(`Unauthorized: ${path}`);
    this.name = "UnauthorizedError";
  }
}

export type FetchWithAuthOptions = RequestInit & {
  /**
   * What a 401 does. `redirect` (the default, and what every existing caller
   * relies on) clears the stale identity and sends the browser to the login
   * page. `throw` raises `UnauthorizedError` and touches NOTHING — no
   * navigation, no identity wipe — for background reads that must not yank a
   * collector out of the page they are on. The wallet's window-focus refresh
   * is the case that motivated it: a reveal mid-ceremony was being replaced
   * by the login page because a cookie lapsed while the tab was backgrounded.
   */
  onUnauthorized?: "redirect" | "throw";
};

/** Fetch an authenticated API resource and recover cleanly from session expiry. */
export async function fetchWithAuth(
  path: string,
  options: FetchWithAuthOptions = {},
): Promise<Response> {
  const { onUnauthorized = "redirect", ...init } = options;
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");

  const res = await fetchApi(apiFetchPath(path), { ...init, headers });
  if (res.status === 401) {
    if (onUnauthorized === "throw") throw new UnauthorizedError(path);
    handleExpiredSession();
  }
  if (res.status === 403) await announcePhoneVerificationRequired(res);
  return res;
}

/** Fired when a deposit or pack purchase is refused for want of a verified
 * phone; PhoneVerificationGate opens on it. */
export const PHONE_VERIFICATION_REQUIRED_EVENT = "mot:phone-verification-required";

async function announcePhoneVerificationRequired(res: Response): Promise<void> {
  if (typeof window === "undefined") return;
  // Read a clone so the caller still gets an unconsumed body.
  const body = await res.clone().json().catch(() => null);
  if ((body as { error?: { code?: string } } | null)?.error?.code === "PHONE_VERIFICATION_REQUIRED") {
    window.dispatchEvent(new Event(PHONE_VERIFICATION_REQUIRED_EVENT));
  }
}

/** Creates a customer account with an explicit, default-off marketing choice. */
export async function signup(
  email: string,
  username: string,
  password: string,
  marketingEmailOptIn: boolean,
  ageAttested: boolean,
  turnstileToken?: string,
): Promise<void> {
  const res = await fetchApi(apiFetchPath("/api/auth/signup"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      username,
      password,
      marketingEmailOptIn,
      ageAttested,
      ...(turnstileToken ? { turnstileToken } : {}),
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(parseApiError(body, "Signup failed"));
  }

}

/** Start a secure cookie session, optionally satisfying an adaptive bot challenge. */
export async function signin(
  email: string,
  password: string,
  turnstileToken?: string,
): Promise<AuthResponse> {
  const res = await fetchApi(apiFetchPath("/api/auth/signin"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      password,
      ...(turnstileToken ? { turnstileToken } : {}),
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(parseApiError(body, "Sign-in failed"));
  }

  const data: AuthResponse = await res.json();
  setCurrentUser(data.user);
  return data;
}

export async function requestPasswordReset(
  email: string,
  turnstileToken?: string,
): Promise<void> {
  const res = await fetchApi(apiFetchPath("/api/auth/password-reset/request"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, ...(turnstileToken ? { turnstileToken } : {}) }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(parseApiError(body, "Could not request a password reset"));
  }
}

export async function completePasswordReset(token: string, password: string): Promise<void> {
  const res = await fetchApi(apiFetchPath("/api/auth/password-reset/complete"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(parseApiError(body, "This password-reset link is invalid or has expired"));
  }
}

export async function completeEmailVerification(token: string): Promise<{ purpose: string }> {
  const res = await fetchApi(apiFetchPath("/api/auth/email-verification/complete"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(parseApiError(body, "This verification link is invalid or has expired"));
  }
  const body = (await res.json().catch(() => ({}))) as { purpose?: string };
  return { purpose: body.purpose ?? "verify_email" };
}

/**
 * Optionally pass the address being verified for a caller with no session yet
 * (e.g. right after signup). Uses `onUnauthorized: "throw"` in that case so a
 * stale/missing session cookie does not bounce the signup page to `/login`.
 */
export async function resendEmailVerification(email?: string): Promise<void> {
  const res = await fetchWithAuth("/api/auth/email-verification/resend", {
    method: "POST",
    body: JSON.stringify(email ? { email } : {}),
    onUnauthorized: email ? "throw" : "redirect",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(parseApiError(body, "Could not resend verification email"));
  }
}

/** Changes the public username (and the display name, which follows it). */
export async function changeUsername(username: string): Promise<void> {
  const res = await fetchWithAuth("/api/auth/username/change", { method: "POST", body: JSON.stringify({ username }) });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(parseApiError(body, "Could not change username"));
  }
}

export async function requestEmailChange(email: string): Promise<void> {
  const res = await fetchWithAuth("/api/auth/email-change/request", { method: "POST", body: JSON.stringify({ email }) });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(parseApiError(body, "Could not request email change"));
  }
}

/** Sets or changes the PSA Vault destination; it only becomes active after its verification link is clicked. */
export async function requestPsaVaultEmail(email: string): Promise<void> {
  const res = await fetchWithAuth("/api/auth/psa-vault-email/request", { method: "POST", body: JSON.stringify({ email }) });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(parseApiError(body, "Could not request PSA Vault email verification"));
  }
}

/** Sends an SMS code. Returns the normalized E.164 number to echo back on confirm. */
export async function startPhoneVerification(phoneNumber: string): Promise<string> {
  const res = await fetchWithAuth("/api/v1/me/phone/start", {
    method: "POST",
    body: JSON.stringify({ phoneNumber }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(parseApiError(body, "Could not send a code to that number"));
  }
  // Confirm must send the same normalized string the server verified against.
  return (body as { data?: { phoneNumber?: string } }).data?.phoneNumber ?? phoneNumber;
}

/** Confirms the SMS code and permanently binds the number to this account. */
export async function confirmPhoneVerification(phoneNumber: string, code: string): Promise<void> {
  const res = await fetchWithAuth("/api/v1/me/phone/confirm", {
    method: "POST",
    body: JSON.stringify({ phoneNumber, code }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(parseApiError(body, "That code is incorrect or expired"));
  }
}

/**
 * Revoke the server-side session before clearing local state. If the
 * request fails, the HttpOnly cookie session is still live server-side, so
 * we must not report a successful signout — throw and let the caller
 * decide how to surface the failure / offer a retry.
 */
export async function signout(): Promise<void> {
  const res = await fetchApi(apiFetchPath("/api/auth/signout"), { method: "POST" });
  if (!res.ok) {
    throw new Error("Sign-out failed. Please try again.");
  }
  removeCurrentUser();
}
